import { useState } from 'react';
import { ActionMenu, ActionList } from '@primer/react';
import { PlusIcon } from '@primer/octicons-react';
import styled from 'styled-components';
import { AddButton, ListToken } from './shared/tokens';
import { SectionContainer, ItemsContainer, ItemsList } from './shared/section-styles';
import { SectionTitleWithGuidance } from './shared/section-guidance';
import { IPRAttributes } from '@upright/common/model/input/companies/products/ipr';
import { ATTRIBUTE_GUIDANCE } from '@upright/common/model/input/companies/products/ipr/attribute-guidance';

export type SpecialEndUseType = keyof IPRAttributes;
interface SpecialEndUse {
  id: SpecialEndUseType;
  title: string;
}

interface SpecialEndUsesProps {
  attributes: SpecialEndUseType[];
  onAttributesChange: (attributes: SpecialEndUseType, value: boolean) => void;
}

const SPECIAL_END_USES: SpecialEndUse[] = [
  { id: 'Educational', title: 'Educational' },
  { id: 'ForHumanitarianAid', title: 'For Humanitarian Aid' },
  { id: 'ForDisabledPeople', title: 'For Disabled People' },
  { id: 'ForLanguageMinorityGroups', title: 'For Language Minority Groups' },
];

export function SpecialEndUses({ attributes, onAttributesChange }: SpecialEndUsesProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const availableAttributes = SPECIAL_END_USES.filter(attribute => !attributes.includes(attribute.id));

  const addAttribute = (attribute: SpecialEndUseType) => {
    onAttributesChange(attribute, true);
    setIsMenuOpen(false);
  };
  const removeAttribute = (attribute: SpecialEndUseType) => {
    onAttributesChange(attribute, false);
  };

  return (
    <SectionContainer>
      <SectionTitleWithGuidance title="Special end uses" guidance={ATTRIBUTE_GUIDANCE.specialEndUses} />

      <ItemsContainer>
        <ItemsList>
          {attributes.map(attributeId => {
            const attribute = SPECIAL_END_USES.find(attr => attr.id === attributeId);
            return (
              <ListToken
                key={attributeId}
                size="medium"
                onRemove={() => {
                  removeAttribute(attributeId);
                }}
                text={attribute?.title || attributeId}
              />
            );
          })}
          <ActionMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <ActionMenu.Anchor>
              <AddButton size="medium" leadingVisual={PlusIcon} text="Add special end use" />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay width="medium">
              <ScrollableActionListContainer>
                <ActionList>
                  <ActionList.Group title="Select a special end use...">
                    {availableAttributes.length > 0 ? (
                      availableAttributes.map(attribute => (
                        <ActionList.Item
                          key={attribute.id}
                          onSelect={() => {
                            addAttribute(attribute.id);
                          }}
                        >
                          {attribute.title}
                        </ActionList.Item>
                      ))
                    ) : (
                      <ActionList.Item disabled>All special end uses added</ActionList.Item>
                    )}
                  </ActionList.Group>
                </ActionList>
              </ScrollableActionListContainer>
            </ActionMenu.Overlay>
          </ActionMenu>
        </ItemsList>
      </ItemsContainer>
    </SectionContainer>
  );
}

const ScrollableActionListContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;
